// Frische Bestätigung DIESER Sitzung (Step-up, docs/anmeldung/SPEZIFIKATION.md §6.4): Verlangt der Server sie
// (`403` mit `details.reason = 'reauth_required'`), fragt der Dialog das Kennwort ab – oder, wenn die Person die
// Zwei-Faktor-Anmeldung nutzt, wahlweise einen Code aus der App – und ruft `POST /api/auth/reauth`. Es entsteht keine
// neue Sitzung; danach gilt die Bestätigung zehn Minuten. Benutzt von `useSecureAction` (Verwaltung) und dem Profil.
//
//   const reauth = useReauth()
//   const codes = await reauth.guard(() => client.recoveryCodes({ code }))   // null = abgebrochen
//   return <>{…}{reauth.dialog}</>
import { AUTH_TEXTS, normalizeTotpCode } from '@shared/auth'
import { toAppError } from '@shared/errors'
import { KeyRound, ShieldCheck, Smartphone } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Modal } from '../../ui/Modal'
import { useApiClient } from '../context'
import { useSession } from '../session'
import { CodeInput } from './CodeInput'
import { codeFailureText, isReauthRequired, passwordFailureText, throttleText } from './errors'
import { reauthStore, resolveReauth } from './reauthStore'
import { TextAction } from './TextAction'

export interface ReauthDialogProps {
  open: boolean
  /** Satz des Servers („Zu Ihrer Sicherheit: …“); Standard AUTH_TEXTS.reauthRequired */
  message?: string
  /** true = bestätigt, false = abgebrochen */
  onResolve: (confirmed: boolean) => void
}

/** Dialog „Mit Kennwort bestätigen“ (bzw. mit Code). Ruft selbst `client.reauth` – bei Erfolg `onResolve(true)`. */
export function ReauthDialog({ open, message, onResolve }: ReauthDialogProps) {
  const client = useApiClient()
  const session = useSession()
  const mfa = session?.security?.mfa === 'on'
  const [mode, setMode] = useState<'password' | 'code'>('password')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reset = (): void => {
    setPassword('')
    setCode('')
    setError(null)
    setMode('password')
  }
  const close = (confirmed: boolean): void => {
    reset()
    onResolve(confirmed)
  }

  const confirm = async (entered: string = code): Promise<void> => {
    if (busy) return
    const totp = normalizeTotpCode(entered)
    if (mode === 'password' && password === '') return setError('Bitte geben Sie Ihr Kennwort ein.')
    if (mode === 'code' && !totp) return setError('Bitte geben Sie den sechsstelligen Code aus Ihrer App ein.')
    setBusy(true)
    setError(null)
    try {
      await client.reauth(mode === 'password' ? { password } : { code: totp as string })
      close(true)
    } catch (cause) {
      const failure = toAppError(cause)
      if (failure.code === 'LIMIT_EXCEEDED') setError(throttleText(failure))
      else setError(mode === 'password' ? passwordFailureText(failure) : codeFailureText(failure))
      if (mode === 'code') setCode('')
    } finally {
      setBusy(false)
    }
  }

  const switchMode = (next: 'password' | 'code'): void => {
    setMode(next)
    setError(null)
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && close(false)}
      title={mode === 'password' ? 'Mit Kennwort bestätigen' : 'Mit Code bestätigen'}
      size="sm"
      data-testid="reauth-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void confirm()} data-testid="reauth-confirm">
            Bestätigen
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          void confirm()
        }}
      >
        <div className="flex items-start gap-2 text-dense text-fg-muted">
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-fg-subtle" strokeWidth={1.75} aria-hidden />
          <p>{message ?? AUTH_TEXTS.reauthRequired}</p>
        </div>
        {mode === 'password' ? (
          <Input
            label={`Kennwort von ${session?.user.displayName ?? 'Ihnen'}`}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            autoFocus
            error={error ?? undefined}
            data-testid="reauth-password"
          />
        ) : (
          <CodeInput value={code} onChange={setCode} onComplete={(complete) => void confirm(complete)} autoFocus error={error ?? undefined} data-testid="reauth-code" />
        )}
        {mfa && (
          <div>
            <TextAction
              icon={mode === 'password' ? Smartphone : KeyRound}
              onClick={() => switchMode(mode === 'password' ? 'code' : 'password')}
              data-testid="reauth-switch"
            >
              {mode === 'password' ? 'Stattdessen mit einem Code aus Ihrer App bestätigen' : 'Stattdessen mit dem Kennwort bestätigen'}
            </TextAction>
          </div>
        )}
      </form>
    </Modal>
  )
}

export interface Reauth {
  /** Dialog zeigen; true = bestätigt */
  ask(message?: string): Promise<boolean>
  /**
   * `fn` ausführen; verlangt der Server eine frische Bestätigung, fragt der Dialog nach und `fn` läuft noch einmal
   * (höchstens dreimal). Liefert `null`, wenn die Person abbricht; andere Fehler gehen unverändert weiter.
   */
  guard<T>(fn: () => Promise<T>): Promise<T | null>
  /** einmal auf der Seite ausgeben */
  dialog: ReactNode
}

export function useReauth(): Reauth {
  const [pending, setPending] = useState<{ message?: string } | null>(null)
  const resolver = useRef<((confirmed: boolean) => void) | null>(null)

  const ask = useCallback(
    (message?: string): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        resolver.current?.(false)
        resolver.current = resolve
        setPending({ message })
      }),
    [],
  )

  const guard = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      for (let attempt = 0; ; attempt += 1) {
        try {
          return await fn()
        } catch (cause) {
          if (!isReauthRequired(cause) || attempt >= 2) throw cause
          if (!(await ask(toAppError(cause).message))) return null
        }
      }
    },
    [ask],
  )

  const dialog = (
    <ReauthDialog
      open={pending !== null}
      message={pending?.message}
      onResolve={(confirmed) => {
        const resolve = resolver.current
        resolver.current = null
        setPending(null)
        resolve?.(confirmed)
      }}
    />
  )

  return { ask, guard, dialog }
}

/**
 * Der EINE Bestätigungsdialog der Anwendung für `requestReauth()` (web/api/auth/reauthStore.ts) – einmal neben
 * `<ActionConfirmHost />`. Damit fragt jede Seite bei `reauth_required` selbst nach, ohne eigenen Dialog.
 */
export function ReauthHost() {
  const request = useSyncExternalStore(reauthStore.subscribe, reauthStore.snapshot, () => null)
  useEffect(() => reauthStore.attach(), [])
  return <ReauthDialog open={request !== null} message={request?.message} onResolve={resolveReauth} />
}
